import { channelSubscribePayload, channelUnsubscribePayload, errorAlert, handshakePayload, handshakeResult, resumePayload, resumeResult, } from '@xdocs/schemas';
import { computeBackoffMs } from './backoff.js';
import { buildReqEnvelope, classifyError, decodeEnvelope, encodeEnvelope, extractOutboxId } from './codec.js';
import { CorrelationRegistry, clientClosedAlert, disconnectedAlert, } from './correlation.js';
import { TypedEmitter } from './emitter.js';
import { noopEnvironment } from './environment.js';
import { generateId } from './id.js';
import { OfflineFlushAbortError, OfflineQueue } from './offline-queue.js';
import { createMemoryClientPersistence, emptyPersistedState, } from './persistence.js';
import { READY_STATE } from './socket-like.js';
const NOT_CONNECTED_ALERT = { code: 'INTERNAL', message: 'errors:ws.not_connected', fields: [] };
/**
 * Framework-agnostic WS client state machine (E17 §5, E20 §5.10). No React;
 * `apps/web` wraps this in a hook/context, but nothing here imports React or
 * touches the DOM directly (see {@link WsClientEnvironment}).
 *
 * Lifecycle: `connect()` → `auth.handshake` → resubscribe channel set →
 * `auth.resume {lastSeenOutboxId}` → `connected`. On drop: `reconnecting`
 * with exponential backoff + full jitter (1s→30s), re-running the same
 * sequence. `online`/`visibilitychange`-to-visible retry immediately instead
 * of waiting out the remaining backoff.
 */
export class WsClient extends TypedEmitter {
    config;
    persistence;
    environment;
    correlation = new CorrelationRegistry();
    offlineQueue;
    requestTimeoutMs;
    heartbeatIntervalMs;
    heartbeatMissLimit;
    now;
    state = 'idle';
    socket = null;
    reconnectAttempt = 0;
    reconnectTimer = null;
    heartbeatTimer = null;
    heartbeatMisses = 0;
    awaitingPong = false;
    closedByUser = false;
    hooksWired = false;
    unwireOnline = null;
    unwireOffline = null;
    unwireVisible = null;
    lastSeenOutboxId = null;
    subscribedChannels = new Set();
    channelHandlers = new Map();
    trackedJobIds = new Set();
    flushPromise = null;
    flushAgainRequested = false;
    constructor(config) {
        super();
        this.config = config;
        this.persistence = config.persistence ?? createMemoryClientPersistence();
        this.environment = config.environment ?? noopEnvironment;
        this.offlineQueue = new OfflineQueue();
        this.requestTimeoutMs = config.requestTimeoutMs ?? 30_000;
        this.heartbeatIntervalMs = config.heartbeatIntervalMs ?? 30_000;
        this.heartbeatMissLimit = config.heartbeatMissLimit ?? 2;
        this.now = config.now ?? Date.now;
    }
    getState() {
        return this.state;
    }
    /**
     * Idempotent: calling `connect()` while already connecting/connected is a
     * no-op. `state` is flipped to `connecting` SYNCHRONOUSLY, before the
     * first `await` — otherwise two back-to-back synchronous `connect()`
     * calls would both pass the guard (the first hasn't updated `state` yet)
     * and race two sockets into existence.
     */
    async connect() {
        if (this.state === 'connecting' || this.state === 'connected' || this.state === 'reconnecting')
            return;
        this.closedByUser = false;
        this.setState('connecting');
        await this.loadPersistedState();
        this.wireEnvironmentHooks();
        this.openSocket();
    }
    /** Deliberate teardown (e.g. logout). Rejects any still-pending requests, including offline-safe ones. */
    disconnect() {
        this.closedByUser = true;
        this.stopHeartbeat();
        this.clearReconnectTimer();
        this.correlation.rejectAll(clientClosedAlert(), { includeOfflineSafe: true });
        this.unwireEnvironmentHooks();
        const socket = this.socket;
        this.socket = null;
        if (socket) {
            socket.onopen = null;
            socket.onmessage = null;
            socket.onerror = null;
            socket.onclose = null;
            try {
                socket.close(1000, 'client disconnect');
            }
            catch {
                // socket already closed/broken — nothing to do
            }
        }
        this.setState('closed');
    }
    /**
     * Typed request with correlation-id matching (§5.3) and a timeout
     * (default 30s). Rejects immediately (fail-closed) if not currently
     * `connected` — use {@link enqueueOffline} for offline-safe optimistic
     * actions instead of blocking on reconnect.
     */
    async request(op, payload, opts) {
        if (this.state !== 'connected' || !this.socket) {
            throw NOT_CONNECTED_ALERT;
        }
        const id = opts?.id ?? generateId();
        const promise = this.correlation.register(id, op, {
            timeoutMs: opts?.timeoutMs ?? this.requestTimeoutMs,
            offlineSafe: false,
        });
        this.sendRaw(buildReqEnvelope({ id, op, payload, ts: this.now() }));
        return promise;
    }
    /**
     * Buffers an offline-safe optimistic action (§5.10-4: mark-read, rename,
     * ... — NEVER an AI job). Returns a promise that resolves/rejects once
     * the action is actually acknowledged by the server, which may be long
     * after this call returns if the socket is currently down — it is
     * flushed automatically, in order, on the next successful reconnect.
     */
    async enqueueOffline(op, payload, id) {
        const item = await this.offlineQueue.enqueue(op, payload, id);
        await this.persistState();
        const promise = this.correlation.register(item.id, op, { timeoutMs: null, offlineSafe: true });
        if (this.state === 'connected')
            void this.flushOfflineQueue();
        return promise;
    }
    /**
     * Subscribes to server pushes on `channel`. Sends `channel.subscribe`
     * once per channel (ref-counted across multiple local subscribers) and
     * persists the channel set for resubscribe-on-reconnect (§5.10-1).
     * Returns an unsubscribe function.
     */
    subscribe(channel, handler) {
        let handlers = this.channelHandlers.get(channel);
        const isNewChannel = !handlers;
        if (!handlers) {
            handlers = new Set();
            this.channelHandlers.set(channel, handlers);
        }
        handlers.add(handler);
        if (isNewChannel) {
            this.subscribedChannels.add(channel);
            void this.persistState();
            if (this.state === 'connected') {
                void this.sendChannelOp('channel.subscribe', channel).catch((err) => {
                    this.emit('error', classifyError(err));
                });
            }
        }
        return () => {
            const set = this.channelHandlers.get(channel);
            set?.delete(handler);
            if (set && set.size === 0) {
                this.channelHandlers.delete(channel);
                this.subscribedChannels.delete(channel);
                void this.persistState();
                if (this.state === 'connected') {
                    void this.sendChannelOp('channel.unsubscribe', channel).catch((err) => {
                        this.emit('error', classifyError(err));
                    });
                }
            }
        };
    }
    /**
     * Marks `jobId` as tracked for reattach (§5.10-2): on every reconnect a
     * `'job-reattach'` event fires for it so the app can re-fetch its current
     * `job_checkpoints`/`jobs.status`/recent `job_logs` once, then rely on the
     * live `job:{jobId}` channel pushes (subscribe to that channel
     * separately via {@link subscribe}).
     */
    trackJob(jobId) {
        if (this.trackedJobIds.has(jobId))
            return;
        this.trackedJobIds.add(jobId);
        void this.persistState();
    }
    untrackJob(jobId) {
        if (!this.trackedJobIds.has(jobId))
            return;
        this.trackedJobIds.delete(jobId);
        void this.persistState();
    }
    // --- connection lifecycle ---------------------------------------------
    setState(next) {
        if (this.state === next)
            return;
        const prev = this.state;
        this.state = next;
        this.emit('state', next, prev);
    }
    async loadPersistedState() {
        const persisted = (await this.persistence.load()) ?? emptyPersistedState();
        this.lastSeenOutboxId = persisted.lastSeenOutboxId;
        for (const channel of persisted.channels)
            this.subscribedChannels.add(channel);
        for (const jobId of persisted.trackedJobIds)
            this.trackedJobIds.add(jobId);
    }
    async persistState() {
        try {
            await this.persistence.save({
                lastSeenOutboxId: this.lastSeenOutboxId,
                channels: Array.from(this.subscribedChannels),
                trackedJobIds: Array.from(this.trackedJobIds),
                offlineQueue: await this.offlineQueue.list(),
            });
        }
        catch (err) {
            this.emit('error', classifyError(err));
        }
    }
    wireEnvironmentHooks() {
        if (this.hooksWired)
            return;
        this.hooksWired = true;
        this.unwireOnline = this.environment.onOnline?.(() => this.handleNetworkBack()) ?? null;
        this.unwireOffline = this.environment.onOffline?.(() => this.handleNetworkGone()) ?? null;
        this.unwireVisible = this.environment.onVisible?.(() => this.handleNetworkBack()) ?? null;
    }
    unwireEnvironmentHooks() {
        this.unwireOnline?.();
        this.unwireOffline?.();
        this.unwireVisible?.();
        this.unwireOnline = null;
        this.unwireOffline = null;
        this.unwireVisible = null;
        this.hooksWired = false;
    }
    handleNetworkGone() {
        // §5.10-1: flips to disconnected status but does NOT tear down client state.
        if (this.state === 'connected' || this.state === 'reconnecting' || this.state === 'connecting') {
            this.setState('offline');
        }
    }
    handleNetworkBack() {
        if (this.closedByUser)
            return;
        // Only jump the queue out of a genuinely-waiting state — 'connecting' is
        // already mid-attempt (don't open a second socket), 'connected'/'idle'/
        // 'closed' have nothing to fast-path.
        if (this.state !== 'reconnecting' && this.state !== 'offline')
            return;
        this.clearReconnectTimer();
        this.reconnectAttempt = 0;
        this.setState('reconnecting');
        this.openSocket();
    }
    openSocket() {
        let socket;
        try {
            socket = this.config.createSocket(this.config.url);
        }
        catch (err) {
            this.emit('error', classifyError(err));
            this.scheduleReconnect();
            return;
        }
        this.socket = socket;
        socket.onopen = () => {
            void this.handshakeAndResume();
        };
        socket.onmessage = (ev) => {
            this.handleMessage(ev.data);
        };
        socket.onerror = () => {
            // The transport-level error event carries no actionable detail beyond
            // "something went wrong" — the definitive signal is the `onclose` that
            // follows, which drives the actual reconnect. Surface it for logging only.
            this.emit('error', { code: 'INTERNAL', message: 'errors:ws.socket_error', fields: [] });
        };
        socket.onclose = () => {
            this.handleDrop();
        };
    }
    async handshakeAndResume() {
        try {
            const accessToken = await this.config.getAccessToken();
            const handshakeReq = handshakePayload.parse({ accessToken });
            const rawHandshake = await this.rawRequest('auth.handshake', handshakeReq);
            const handshake = handshakeResult.parse(rawHandshake);
            this.emit('authenticated', handshake);
            // `allSettled`, NOT `all` (BUG FOUND via fidelity-gate e2e review — a
            // "connection death spiral"): a channel the server rejects (e.g.
            // `FORBIDDEN`/`NOT_FOUND` — a nonexistent or no-longer-authorized
            // document, seen live navigating straight to `/documents/:id` for a
            // deleted/bad id) used to reject this whole `Promise.all`, which the
            // `catch` below treated as a handshake failure and closed the socket
            // — `handleDrop` -> `scheduleReconnect` -> `openSocket` -> the SAME
            // persisted `subscribedChannels` set (never pruned) resubscribes the
            // SAME failing channel and fails the SAME way, forever: `connected`
            // was never reachable again, `connectionState` stuck on
            // `'reconnecting'` permanently, every gated `useQuery` (`enabled:
            // connectionState === 'connected'`) starved for good. One
            // permission/lookup error on ONE channel must never take the whole
            // socket down — surface it (logging/telemetry) and keep going; the
            // other channels + the eventual `document.get`/etc. requests this
            // unblocks are what actually matter to the page.
            const subscribeResults = await Promise.allSettled(Array.from(this.subscribedChannels).map((channel) => this.sendChannelOp('channel.subscribe', channel)));
            for (const result of subscribeResults) {
                if (result.status === 'rejected')
                    this.emit('error', classifyError(result.reason));
            }
            const resumeReq = resumePayload.parse({ lastSeenOutboxId: this.lastSeenOutboxId });
            const rawResume = await this.rawRequest('auth.resume', resumeReq);
            const resume = resumeResult.parse(rawResume);
            if (resume.gap)
                this.emit('resume-gap');
            const wasReconnecting = this.state === 'reconnecting' || this.state === 'offline';
            this.reconnectAttempt = 0;
            this.setState('connected');
            if (wasReconnecting)
                this.emit('back-online');
            for (const jobId of this.trackedJobIds)
                this.emit('job-reattach', jobId);
            this.startHeartbeat();
            void this.flushOfflineQueue();
        }
        catch (err) {
            this.emit('error', classifyError(err));
            try {
                this.socket?.close();
            }
            catch {
                // already closing/closed
            }
        }
    }
    handleDrop() {
        this.stopHeartbeat();
        this.socket = null;
        this.correlation.rejectAll(disconnectedAlert());
        if (this.closedByUser) {
            this.setState('closed');
            return;
        }
        this.scheduleReconnect();
    }
    /** Schedules the next `openSocket()` attempt with backoff, and reflects it in `state`. */
    scheduleReconnect() {
        this.clearReconnectTimer();
        // Don't stomp an `offline` status with `reconnecting` — the environment
        // already told us why we're not connected; `handleNetworkBack` fast-paths
        // out of `offline` the moment connectivity actually returns.
        if (!this.closedByUser && this.state !== 'offline')
            this.setState('reconnecting');
        const attempt = this.reconnectAttempt;
        this.reconnectAttempt += 1;
        const delay = computeBackoffMs(attempt, this.config.backoff);
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.openSocket();
        }, delay);
    }
    clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }
    // --- messaging -----------------------------------------------------------
    sendRaw(frame) {
        if (!this.socket || this.socket.readyState !== READY_STATE.OPEN) {
            throw NOT_CONNECTED_ALERT;
        }
        this.socket.send(encodeEnvelope(frame));
    }
    /** Used internally during handshake/resume/subscribe, before `state` flips to `connected`. */
    rawRequest(op, payload) {
        const id = generateId();
        const promise = this.correlation.register(id, op, {
            timeoutMs: this.requestTimeoutMs,
            offlineSafe: false,
        });
        this.sendRaw(buildReqEnvelope({ id, op, payload, ts: this.now() }));
        return promise;
    }
    async sendChannelOp(op, channel) {
        const schema = op === 'channel.subscribe' ? channelSubscribePayload : channelUnsubscribePayload;
        const payload = schema.parse({ channel });
        await this.rawRequest(op, payload);
    }
    handleMessage(raw) {
        const decoded = decodeEnvelope(raw);
        if (!decoded.ok) {
            this.emit('error', decoded.alert);
            return;
        }
        const envelope = decoded.envelope;
        switch (envelope.type) {
            case 'res': {
                if (envelope.id)
                    this.correlation.resolve(envelope.id, envelope.payload);
                break;
            }
            case 'err': {
                if (envelope.id) {
                    const parsed = errorAlert.safeParse(envelope.payload);
                    this.correlation.reject(envelope.id, parsed.success ? parsed.data : { code: 'INTERNAL', message: 'errors:internal.generic', fields: [] });
                }
                break;
            }
            case 'ack': {
                if (envelope.id)
                    this.correlation.resolve(envelope.id, envelope.payload);
                break;
            }
            case 'push': {
                const outboxId = extractOutboxId(envelope);
                if (outboxId) {
                    this.lastSeenOutboxId = outboxId;
                    void this.persistState();
                }
                if (envelope.channel) {
                    const handlers = this.channelHandlers.get(envelope.channel);
                    if (handlers) {
                        for (const handler of Array.from(handlers))
                            handler(envelope);
                    }
                }
                this.emit('push', envelope);
                break;
            }
            case 'ping': {
                this.trySendRaw({ type: 'pong', ts: this.now() });
                break;
            }
            case 'pong': {
                this.awaitingPong = false;
                this.heartbeatMisses = 0;
                break;
            }
            case 'req': {
                // The client never receives `req` frames — a well-behaved server never
                // sends one. Ignore rather than crash on an unexpected-but-valid envelope.
                break;
            }
        }
    }
    trySendRaw(frame) {
        try {
            this.sendRaw(frame);
        }
        catch (err) {
            this.emit('error', classifyError(err));
        }
    }
    // --- heartbeat (§5.7) ------------------------------------------------------
    startHeartbeat() {
        this.stopHeartbeat();
        this.heartbeatMisses = 0;
        this.awaitingPong = false;
        this.heartbeatTimer = setInterval(() => {
            if (this.awaitingPong) {
                this.heartbeatMisses += 1;
            }
            if (this.heartbeatMisses >= this.heartbeatMissLimit) {
                this.emit('error', { code: 'INTERNAL', message: 'errors:ws.heartbeat_timeout', fields: [] });
                try {
                    this.socket?.close();
                }
                catch {
                    // already closing/closed — onclose (if it fires) drives the reconnect
                }
                return;
            }
            this.trySendRaw({ type: 'ping', ts: this.now() });
            this.awaitingPong = true;
        }, this.heartbeatIntervalMs);
    }
    stopHeartbeat() {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
        this.heartbeatMisses = 0;
        this.awaitingPong = false;
    }
    // --- offline queue (§5.10-4) -------------------------------------------
    /**
     * Single-flight: overlapping triggers (handshake completing while a
     * caller also just enqueued a new item) share one in-flight flush instead
     * of racing two loops over the same underlying queue. If an item is
     * enqueued while a flush is running, one more flush pass runs after the
     * current one settles so it isn't left stranded until the next reconnect.
     */
    async flushOfflineQueue() {
        if (this.flushPromise) {
            this.flushAgainRequested = true;
            return this.flushPromise;
        }
        this.flushPromise = this.runFlush();
        try {
            await this.flushPromise;
        }
        finally {
            this.flushPromise = null;
        }
        if (this.flushAgainRequested) {
            this.flushAgainRequested = false;
            if (this.state === 'connected')
                await this.flushOfflineQueue();
        }
    }
    async runFlush() {
        await this.offlineQueue.flush(async (item) => {
            if (this.state !== 'connected' || !this.socket)
                throw new OfflineFlushAbortError();
            const promise = this.correlation.register(item.id, item.op, { timeoutMs: null, offlineSafe: true });
            try {
                this.sendRaw(buildReqEnvelope({ id: item.id, op: item.op, payload: item.payload, ts: this.now() }));
            }
            catch {
                throw new OfflineFlushAbortError();
            }
            try {
                // Racing against `state` leaving 'connected' is what makes a drop
                // WHILE awaiting this specific item's reply abort the flush loop
                // immediately, instead of hanging forever on a promise that
                // `correlation.rejectAll` deliberately never touches (offlineSafe).
                const result = await this.raceWhileConnected(promise);
                this.emit('offline-flushed', item, { ok: true, result });
                return result;
            }
            catch (alert) {
                if (alert instanceof OfflineFlushAbortError)
                    throw alert;
                this.emit('offline-flushed', item, { ok: false, alert });
                throw alert;
            }
        });
        await this.persistState();
    }
    /**
     * Resolves/rejects with `promise`, but rejects early with
     * {@link OfflineFlushAbortError} the moment `state` leaves `'connected'`
     * (e.g. the socket drops again while this specific item's reply is still
     * in flight). Without this, a drop mid-await would leave the offline
     * queue's flush loop — and the single-flight `flushPromise` guard —
     * wedged forever, since the awaited entry is `offlineSafe` and therefore
     * deliberately exempt from `correlation.rejectAll`.
     */
    raceWhileConnected(promise) {
        if (this.state !== 'connected')
            return Promise.reject(new OfflineFlushAbortError());
        let unsubscribe = null;
        const abort = new Promise((_resolve, reject) => {
            unsubscribe = this.on('state', (next) => {
                if (next !== 'connected')
                    reject(new OfflineFlushAbortError());
            });
        });
        return Promise.race([promise, abort]).finally(() => unsubscribe?.());
    }
}
